import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { FormField } from "./FormField";
import { fetchRequestWithAuth } from "@/lib/api-client";
import { getAdminImagesUploadUrl } from "@/lib/config";
import { getRecordString } from "@/lib/type-guards";

interface ImagePoolFieldProps {
  id: string;
  label: string;
  value: string[];
  onChange: (urls: string[]) => void;
  error?: string;
  helperText?: string;
}

export function ImagePoolField({
  id,
  label,
  value,
  onChange,
  error,
  helperText,
}: ImagePoolFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadError(null);
    try {
      const url = getAdminImagesUploadUrl();

      const uploadPromises = Array.from(files).map(async (file) => {
        const headers = new Headers();
        headers.set("Content-Type", file.type || "image/jpeg");

        const request = new Request(url, { method: "POST", headers, body: file });
        const response = await fetchRequestWithAuth(request);

        if (!response.ok) {
          let errorMessage = "Failed to upload image";
          try {
            const errorData: unknown = await response.json();
            const message = getRecordString(errorData, "error");
            if (message) {
              errorMessage = message;
            }
          } catch {
            // If JSON parsing fails, use default message
          }
          throw new Error(errorMessage);
        }

        const result: unknown = await response.json();
        const imageUrl = getRecordString(result, "imageUrl");
        if (!imageUrl) {
          throw new Error("Upload succeeded but no image URL was returned");
        }
        return imageUrl;
      });

      const newUrls = await Promise.all(uploadPromises);
      onChange([...value, ...newUrls]);
      setUploadError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to upload images";
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeImage = (index: number) => {
    const newValue = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
  };

  return (
    <FormField
      id={id}
      label={label}
      error={error || uploadError || undefined}
      helperText={helperText || "Upload one or more images for the event series"}
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-wrap gap-2">
          {value.map((url, index) => (
            <div
              key={index}
              className="relative"
              style={{ width: "120px", height: "120px" }}
            >
              <img
                src={url}
                alt={`Pool ${index}`}
                className="w-full h-full border-round shadow-1"
                style={{ objectFit: "cover" }}
              />
              <Button
                type="button"
                icon="pi pi-times"
                className="p-button-rounded p-button-danger p-button-xs absolute top-0 right-0 m-1"
                onClick={() => removeImage(index)}
                aria-label="Remove image"
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                }}
              />
            </div>
          ))}
          <div
            className="flex align-items-center justify-content-center border-2 border-dashed border-300 border-round cursor-pointer hover:border-primary transition-colors"
            style={{ width: "120px", height: "120px" }}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="pi pi-plus text-2xl text-400"></i>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                void handleUpload(e);
              }}
              disabled={uploading}
            />
          </div>
        </div>
        {uploading && (
          <div className="flex align-items-center gap-2">
            <i className="pi pi-spin pi-spinner text-primary"></i>
            <span className="text-sm text-600">Uploading images...</span>
          </div>
        )}
      </div>
    </FormField>
  );
}
